import { type StateCreator } from "zustand";

import { chatThreadsService, type TChatMessage, type TChatThread, type TChatThreadCreatePayload } from "@/api/chat";
import { TOnboardingSummary } from "@/api/onboarding";
import { TChat, TFolder, TMessage, TMessageFile } from "@/types/chat";
import { apiURL } from "@/utils";
import { getSessionExpiredAwareErrorMessage, isSessionExpiredHttpError } from "@/utils/authUtils";
import { normalizeOnboardingSummary } from "@/utils/onboardingSummary";

export type TDashboardContext = {
  companyId: string;
  companyName?: string;
  sectionId: string | null;
  sectionName: string;
  description?: string | null;
};

export type TChatThreadContext = {
  threadId: string | null;
  companyId: string;
  companyName?: string;
  sectionId: string | null;
  sectionName: string;
  title?: string | null;
  initialPrompt?: string | null;
  /** File IDs from thread file uploads to send with the first message */
  initialFileIds?: string[] | null;
};

export type TCompanyKnowledgeContext = {
  companyId: string;
  companyName?: string | null;
};

export type TCreateThreadResult = {
  success: boolean;
  thread?: TChatThread;
  error?: string;
};

export type TFetchMessagesResult = {
  success: boolean;
  messages?: TChatMessage[];
  error?: string;
};

export type TInvestorChatThread = {
  id: string;
  title: string;
  founder_company_id: string;
  founder_name: string;
  created_at?: string;
};

export type TChatStoreState = {
  folders: TFolder[];
  activeChatId: string | null;
  isOnboardingActive: boolean;
  currentProject: string | null;
  isCompanyKnowledgeOpen: boolean;
  companyKnowledgeContext: TCompanyKnowledgeContext | null;
  lastCompletedCompanyId: string | null;
  activeDashboard: TDashboardContext | null;
  activeChatThread: TChatThreadContext | null;
  threadTitles: Record<string, string>;
  /** Investor chat threads for deal pipeline section, keyed by companyId:sectionId */
  investorChatThreads: Record<string, TInvestorChatThread[]>;
  // Thread operation states
  isCreatingThread: boolean;
  createThreadError: string | null;
  isFetchingMessages: boolean;
  fetchMessagesError: string | null;
  threadMessages: Record<string, TChatMessage[]>;
  // Cached thread messages (TMessage format for UI)
  cachedThreadMessages: Record<string, TMessage[]>;
};

export type TChatStoreActions = {
  addChat: (folderId: string, chatTitle: string) => void;
  renameChat: (chatId: string, newTitle: string) => void;
  deleteChat: (chatId: string) => void;
  addFolder: (folderName: string) => void;
  renameFolder: (folderId: string, newName: string) => void;
  deleteFolder: (folderId: string) => void;
  addMessage: (chatId: string, text: string, files?: TMessageFile[]) => void;
  setActiveChatId: (chatId: string | null) => void;
  startOnboarding: () => void;
  cancelOnboarding: () => void;
  completeOnboarding: (payload: { projectName?: string; companyId?: string }) => void;
  toggleFolderCollapse: (folderId: string) => void;
  setCurrentProject: (projectId: string | null) => void;
  openCompanyKnowledge: (context: TCompanyKnowledgeContext) => void;
  closeCompanyKnowledge: () => void;
  setOnboardingSummaryMessages: (payload: { companyId: string; summary: TOnboardingSummary }) => void;
  openDashboard: (context: TDashboardContext) => void;
  closeDashboard: () => void;
  openChatThreadSession: (context: TChatThreadContext) => void;
  closeChatThreadSession: () => void;
  clearThreadInitialPrompt: () => void;
  setThreadTitle: (threadId: string, title: string) => void;
  clearLastCompletedCompanyId: () => void;
  addInvestorChatThread: (companyId: string, sectionId: string, thread: TInvestorChatThread) => void;
  getInvestorChatThreads: (companyId: string, sectionId: string) => TInvestorChatThread[];
  // Thread API actions
  createThread: (payload: TChatThreadCreatePayload) => Promise<TCreateThreadResult>;
  fetchThreadMessages: (threadId: string, limit?: number) => Promise<TFetchMessagesResult>;
  getThreadMessages: (threadId: string) => TChatMessage[];
  // Cached thread messages (TMessage format for UI)
  setCachedThreadMessages: (threadId: string, messages: TMessage[]) => void;
  getCachedThreadMessages: (threadId: string) => TMessage[];
};

// Sample messages to demonstrate features
const sampleMessages: TMessage[] = [
  {
    id: "msg-1",
    text: "What's your company name?",
    sender: "ai",
  },
  {
    id: "msg-2",
    text: "What problem are you solving and for whom?",
    sender: "ai",
    helperText: "(Type response or upload target market, user personas with pain points)",
  },
  {
    id: "msg-3",
    text: "Finas",
    sender: "user",
  },
  {
    id: "msg-4",
    text: "We help parents in peri-urban areas pay school fees in flexible micro-instalments so students don't miss class.",
    sender: "user",
    files: [
      {
        id: "file-1",
        name: "User Personas_KenyaSchools.pdf",
        url: "#",
      },
    ],
  },
  {
    id: "msg-5",
    text: "Why will you win this market, what's your unfair advantage? Why now?",
    sender: "ai",
    helperText:
      "(Type response or upload competitive analysis/market research, market positioning, tech documentation, partnership agreements, patents, market timing thesis)",
  },
  {
    id: "msg-6",
    text: "We partner directly with school bursars for distribution, lowering CAC vs digital lenders. Digital payments adoption has spiked post-COVID.",
    sender: "user",
  },
];

const initialChatStoreState: TChatStoreState = {
  folders: [
    {
      id: "1",
      name: "Chats",
      chats: [
        {
          id: "1-1",
          title: "Onboarding Summary",
          messages: sampleMessages,
        },
        { id: "1-2", title: "Chat about feature X", messages: [] },
      ],
      collapsed: false,
    },
    {
      id: "2",
      name: "Investment Readiness",
      chats: [{ id: "2-1", title: "Market Analysis", messages: [] }],
      collapsed: true,
      isCategory: true,
    },
    {
      id: "3",
      name: "My Deal Sack",
      chats: [],
      collapsed: true,
      isCategory: true,
    },
    {
      id: "4",
      name: "My Deal Network",
      chats: [],
      collapsed: true,
      isCategory: true,
    },
    {
      id: "5",
      name: "Keja",
      chats: [],
      collapsed: true,
    },
  ],
  activeChatId: null,
  isOnboardingActive: false,
  currentProject: "finas",
  isCompanyKnowledgeOpen: false,
  companyKnowledgeContext: null,
  lastCompletedCompanyId: null,
  activeDashboard: null,
  activeChatThread: null,
  threadTitles: {},
  investorChatThreads: {},
  // Thread operation states
  isCreatingThread: false,
  createThreadError: null,
  isFetchingMessages: false,
  fetchMessagesError: null,
  threadMessages: {},
  // Cached thread messages (TMessage format for UI)
  cachedThreadMessages: {},
};

const buildOnboardingSummaryMessages = (_companyId: string, summary: TOnboardingSummary): TMessage[] => {
  const messages: TMessage[] = [];

  // Convert history array into chat messages
  // Questions go on the left (ai sender), answers go on the right (user sender)
  // Handle case where history might be undefined (when status is "processing")
  if (!summary.history) {
    return messages;
  }

  summary.history.forEach((item) => {
    // Add question as AI message (left side)
    messages.push({
      id: `question-${item.id}`,
      text: item.question,
      sender: "ai",
      createdAt: item.timestamp,
    });

    // Add answer as user message (right side) if it exists
    // Check all answer types: text, number, choice, file
    const hasText = Boolean(item.has_text && item.answer_text && String(item.answer_text).trim().length > 0);
    const hasNumber = Boolean(item.has_number && item.answer_number !== null && item.answer_number !== undefined);
    const hasChoice = Boolean(item.has_choice && item.answer_choice);
    const hasFile = Boolean(item.has_file && item.file_id);

    // Determine if answer has any content (at least one type filled)
    const hasAnswer = hasText || hasNumber || hasChoice || hasFile;

    if (hasAnswer) {
      // Prepare file information if available
      const files: TMessageFile[] = [];
      if (hasFile && item.file_id) {
        const fileName = item.file_name ? String(item.file_name) : "uploaded-file";
        const rawUrl = item.file_url ? String(item.file_url) : null;
        const fileUrl = rawUrl?.startsWith("/") ? apiURL(rawUrl).toString() : (rawUrl ?? "#");
        files.push({
          id: String(item.file_id),
          name: fileName,
          url: fileUrl,
          contentType: item.content_type ?? null,
          fileSize: item.file_size ?? null,
        });
      }

      // Build message text based on answer type
      let messageText = "";
      if (hasText) {
        messageText = String(item.answer_text ?? "");
      } else if (hasNumber) {
        // Format number appropriately (could be currency, percentage, etc.)
        const numValue = item.answer_number;
        messageText = typeof numValue === "number" ? String(numValue) : "";
      } else if (hasChoice) {
        messageText = String(item.answer_choice ?? "");
      } else if (hasFile) {
        messageText = `Uploaded file: ${item.file_name ?? "file"}`;
      }

      messages.push({
        id: `answer-${item.id}`,
        text: messageText,
        sender: "user",
        files: files.length > 0 ? files : undefined,
        createdAt: item.timestamp,
      });
    }
  });

  return messages;
};

export type TChatStore = TChatStoreState & TChatStoreActions;

export const createChatSlice: StateCreator<TChatStore> = (set, get) => ({
  ...initialChatStoreState,
  addChat: (folderId, chatTitle) =>
    set((state) => {
      const newChat: TChat = {
        id: Date.now().toString(),
        title: chatTitle,
        messages: [],
      };
      const updatedFolders = state.folders.map((folder) =>
        folder.id === folderId ? { ...folder, chats: [...folder.chats, newChat] } : folder
      );
      return { folders: updatedFolders, activeChatId: newChat.id };
    }),
  renameChat: (chatId, newTitle) =>
    set((state) => {
      const updatedFolders = state.folders.map((folder) => ({
        ...folder,
        chats: folder.chats.map((chat) => (chat.id === chatId ? { ...chat, title: newTitle } : chat)),
      }));
      return { folders: updatedFolders };
    }),
  deleteChat: (chatId) =>
    set((state) => {
      const updatedFolders = state.folders.map((folder) => ({
        ...folder,
        chats: folder.chats.filter((chat) => chat.id !== chatId),
      }));
      return {
        folders: updatedFolders,
        activeChatId: state.activeChatId === chatId ? null : state.activeChatId,
      };
    }),
  addFolder: (folderName) =>
    set((state) => {
      const newFolder: TFolder = {
        id: Date.now().toString(),
        name: folderName,
        chats: [],
        collapsed: true,
      };
      return { folders: [...state.folders, newFolder] };
    }),
  renameFolder: (folderId, newName) =>
    set((state) => ({
      folders: state.folders.map((folder) => (folder.id === folderId ? { ...folder, name: newName } : folder)),
    })),
  deleteFolder: (folderId) => set((state) => ({ folders: state.folders.filter((folder) => folder.id !== folderId) })),
  addMessage: (chatId, text, files) =>
    set((state) => {
      const newMessage: TMessage = {
        id: Date.now().toString(),
        text,
        sender: "user",
        ...(files && files.length > 0 && { files }),
      };
      const updatedFolders = state.folders.map((folder) => ({
        ...folder,
        chats: folder.chats.map((chat) =>
          chat.id === chatId ? { ...chat, messages: [...chat.messages, newMessage] } : chat
        ),
      }));
      return { folders: updatedFolders };
    }),
  setActiveChatId: (chatId) =>
    set({
      activeChatId: chatId,
      isOnboardingActive: false,
      isCompanyKnowledgeOpen: false,
      activeDashboard: null,
      activeChatThread: null,
    }),
  startOnboarding: () =>
    set({
      activeChatId: null,
      isOnboardingActive: true,
      isCompanyKnowledgeOpen: false,
      activeDashboard: null,
      activeChatThread: null,
    }),
  cancelOnboarding: () => set({ isOnboardingActive: false, activeDashboard: null, activeChatThread: null }),
  openCompanyKnowledge: (context) =>
    set((state) => ({
      isCompanyKnowledgeOpen: true,
      isOnboardingActive: state.isOnboardingActive,
      companyKnowledgeContext: context,
    })),
  closeCompanyKnowledge: () => set({ isCompanyKnowledgeOpen: false, companyKnowledgeContext: null }),
  setOnboardingSummaryMessages: ({ companyId, summary }) =>
    set((state) => {
      const normalizedSummary = normalizeOnboardingSummary(summary.summary, summary.company_role) || undefined;
      const existingChat = state.folders[0]?.chats.find((chat) => chat.id === "1-1");
      const existingSummary = existingChat?.metadata?.summary;
      const existingCompanyId = existingChat?.metadata?.companyId;
      if (
        existingSummary &&
        existingSummary === normalizedSummary &&
        existingCompanyId === companyId &&
        state.activeChatId === "1-1"
      ) {
        return state;
      }

      const summaryMessages = buildOnboardingSummaryMessages(companyId, summary);

      const updatedFolders = state.folders.map((folder, index) => {
        if (index !== 0) {
          return folder;
        }
        return {
          ...folder,
          chats: folder.chats.map((chat) =>
            chat.id === "1-1"
              ? {
                  ...chat,
                  title: summary.company_name ? `Onboarding Summary - ${summary.company_name}` : "Onboarding Summary",
                  messages: summaryMessages,
                  metadata: {
                    summary: normalizedSummary,
                    rawSummary: typeof summary.summary === "string" ? summary.summary : undefined,
                    companyId: companyId,
                    companyName: summary.company_name,
                    companyRole: summary.company_role,
                    imageFiles: summary.image_files ?? [],
                    allFiles: summary.all_files ?? summary.image_files ?? [],
                  },
                }
              : chat
          ),
        };
      });

      return {
        folders: updatedFolders,
        activeChatId: "1-1",
        activeDashboard: null,
        activeChatThread: null,
      };
    }),
  completeOnboarding: ({ projectName, companyId }) =>
    set((state) => {
      const targetFolderId = state.folders[0]?.id;

      if (!targetFolderId) {
        return {
          isOnboardingActive: false,
          activeChatId: null,
          isCompanyKnowledgeOpen: false,
          activeDashboard: null,
          activeChatThread: null,
        };
      }

      const newChat: TChat = {
        id: Date.now().toString(),
        title: projectName && projectName.trim().length > 0 ? projectName : "New Project",
        messages: [],
      };

      const updatedFolders = state.folders.map((folder) =>
        folder.id === targetFolderId ? { ...folder, chats: [...folder.chats, newChat] } : folder
      );

      return {
        folders: updatedFolders,
        activeChatId: newChat.id,
        isOnboardingActive: false,
        isCompanyKnowledgeOpen: false,
        lastCompletedCompanyId: companyId ?? null,
        activeDashboard: null,
        activeChatThread: null,
      };
    }),
  toggleFolderCollapse: (folderId) =>
    set((state) => ({
      folders: state.folders.map((folder) =>
        folder.id === folderId ? { ...folder, collapsed: !folder.collapsed } : folder
      ),
    })),
  setCurrentProject: (projectId) => set({ currentProject: projectId }),
  openDashboard: (context) =>
    set({
      activeDashboard: context,
      activeChatId: null,
      isOnboardingActive: false,
      activeChatThread: null,
    }),
  closeDashboard: () => set({ activeDashboard: null }),
  openChatThreadSession: (context) =>
    set(() => {
      // When opening a new chat (threadId is null), clear cached messages immediately
      // This ensures messages are cleared at the source before the hook even runs
      if (!context.threadId) {
        return {
          activeChatThread: context,
          activeChatId: null,
          isOnboardingActive: false,
          cachedThreadMessages: {}, // Clear all cached messages for new chats
        };
      }
      return {
        activeChatThread: context,
        activeChatId: null,
        isOnboardingActive: false,
      };
    }),
  closeChatThreadSession: () => set({ activeChatThread: null }),
  clearThreadInitialPrompt: () =>
    set((state) => ({
      activeChatThread: state.activeChatThread
        ? { ...state.activeChatThread, initialPrompt: null, initialFileIds: null }
        : null,
    })),
  setThreadTitle: (threadId, title) =>
    set((state) => ({
      threadTitles: {
        ...state.threadTitles,
        [threadId]: title,
      },
    })),
  clearLastCompletedCompanyId: () => set({ lastCompletedCompanyId: null }),

  addInvestorChatThread: (companyId, sectionId, thread) =>
    set((state) => {
      const key = `${companyId}:${sectionId}`;
      const existing = state.investorChatThreads[key] ?? [];
      if (existing.some((t) => t.id === thread.id)) return state;
      return {
        investorChatThreads: {
          ...state.investorChatThreads,
          [key]: [...existing, thread].sort((a, b) => {
            const da = a.created_at ? new Date(a.created_at).getTime() : 0;
            const db = b.created_at ? new Date(b.created_at).getTime() : 0;
            return db - da;
          }),
        },
      };
    }),

  getInvestorChatThreads: (companyId, sectionId) => {
    const key = `${companyId}:${sectionId}`;
    return get().investorChatThreads[key] ?? [];
  },

  // Thread API actions
  createThread: async (payload) => {
    set({ isCreatingThread: true, createThreadError: null });

    try {
      const { data, error } = await chatThreadsService.createThread(payload);

      if (error) {
        const errorMessage = isSessionExpiredHttpError(error)
          ? null
          : await getSessionExpiredAwareErrorMessage(error, "Failed to create thread");
        set({
          isCreatingThread: false,
          createThreadError: errorMessage,
        });
        return { success: false, error: errorMessage ?? "Failed to create thread" };
      }

      if (data?.data) {
        const thread = data.data;
        // Update thread title in store
        set((state) => ({
          isCreatingThread: false,
          createThreadError: null,
          threadTitles: {
            ...state.threadTitles,
            [thread.id]: thread.title ?? "New Chat",
          },
        }));
        return { success: true, thread };
      }

      set({ isCreatingThread: false });
      return { success: false, error: "No data returned" };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create thread";
      set({
        isCreatingThread: false,
        createThreadError: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  },

  fetchThreadMessages: async (threadId, limit = 50) => {
    set({ isFetchingMessages: true, fetchMessagesError: null });

    try {
      const { data, error } = await chatThreadsService.getThreadMessages(threadId, limit);

      if (error) {
        const errorMessage = isSessionExpiredHttpError(error)
          ? null
          : await getSessionExpiredAwareErrorMessage(error, "Failed to fetch messages");
        set({
          isFetchingMessages: false,
          fetchMessagesError: errorMessage,
        });
        return { success: false, error: errorMessage ?? "Failed to fetch messages" };
      }

      if (data?.data?.messages) {
        const messages = data.data.messages;
        set((state) => ({
          isFetchingMessages: false,
          fetchMessagesError: null,
          threadMessages: {
            ...state.threadMessages,
            [threadId]: messages,
          },
        }));
        return { success: true, messages };
      }

      set({ isFetchingMessages: false });
      return { success: false, error: "No messages returned" };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch messages";
      set({
        isFetchingMessages: false,
        fetchMessagesError: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  },

  getThreadMessages: (threadId) => {
    return get().threadMessages[threadId] ?? [];
  },

  // Cached thread messages (TMessage format for UI)
  setCachedThreadMessages: (threadId, messages) =>
    set((state) => ({
      cachedThreadMessages: {
        ...state.cachedThreadMessages,
        [threadId]: messages,
      },
    })),

  getCachedThreadMessages: (threadId) => {
    return get().cachedThreadMessages[threadId] ?? [];
  },
});
